import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassTabs - Glass morphism tab navigation
 *
 * A polished tab navigation component with glass container and active state styling.
 * Designed for section navigation within pages.
 *
 * @example
 * ```tsx
 * <GlassTabs
 *   tabs={[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'milestones', label: 'Milestones', icon: <CalendarIcon /> },
 *     { id: 'features', label: 'Features' },
 *   ]}
 *   activeTab="overview"
 *   onTabChange={(tabId) => setActiveTab(tabId)}
 * />
 * ```
 */

/** Single tab definition */
export interface GlassTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface GlassTabsProps {
  /** Array of tab definitions */
  tabs: GlassTab[];
  /** Currently active tab ID */
  activeTab: string;
  /** Callback when tab is changed */
  onTabChange: (tabId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * GlassTabs Component
 *
 * Features:
 * - Glass container with inline-flex layout
 * - Active tab with white background and shadow
 * - Smooth transitions on tab changes
 * - Optional icon support
 * - Keyboard accessible
 */
export const GlassTabs: React.FC<GlassTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
}) => {
  return (
    <div className={cn('glass rounded-xl p-1.5 inline-flex gap-1', className)}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              {
                'tab-active text-foreground': isActive,
                'text-muted-foreground/60 hover:text-muted-foreground hover:bg-white/50 dark:hover:bg-white/10': !isActive,
              }
            )}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
          >
            <span className="flex items-center gap-2">
              {tab.icon && <span className="w-4 h-4">{tab.icon}</span>}
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

GlassTabs.displayName = 'GlassTabs';
